// iceTransportPolicy — CU-869dafa5q follow-up (BBB pattern #2 subset).
//
// Operations-grade kill switch for forcing TURN-only (relay) call paths
// without a deploy. When enabled, sipService threads
// `iceTransportPolicy: 'relay'` into the RTCPeerConnection config so the
// browser refuses to use host/srflx candidates and uses only relay
// candidates from the configured TURN servers.
//
// Why this exists: customers behind broken NAT / symmetric firewalls /
// VPNs sometimes negotiate a "successful" ICE pair on a host-host or
// host-srflx path that drops RTP after a few seconds. Forcing relay
// trades a few hundred ms of mouth-to-ear latency for reliable media.
// BBB's audio bridge ships this as `media.audio.retryThroughRelay` and
// flips it on tenant retries. We expose it as a runtime flag so ops can
// enable per-user (URL override on the support call) or per-tenant
// (remote config injecting `?relay=on` into the page URL).
//
// Default: off — direct paths work for the majority of calls and
// forcing relay raises TURN bandwidth costs.
//
// Operator interface (mirrors the diagnostics kill switch):
//   - URL: `?relay=on` enables and stores. `?relay=off` disables and
//     stores. `?relay=auto` clears the sticky.
//   - localStorage: `phq_force_relay` = `'on'` | `'off'` is read live.
//   - Default: off.

const RELAY_STORAGE_KEY = 'phq_force_relay';

let urlOverrideProcessed = false;
// In-memory record of the URL override. Codex review — localStorage can
// throw or silently fail (privacy mode, sandboxed iframes, enterprise
// policy), and the failure modes are asymmetric (setItem may throw
// while getItem still returns stale values). The model:
//
//   'on'/'off' — URL set a value AND storage write failed. Storage is
//                unreliable; trust this in-memory value.
//   'clear'   — URL set ?relay=auto AND storage removal failed. Storage
//                may still return a stale value; suppress it.
//   null      — Storage write succeeded (or no URL override given);
//                storage is the source of truth.
let urlOverrideInMemory: 'on' | 'off' | 'clear' | null = null;

function readRelayFlagFromUrl(): 'on' | 'off' | 'auto' | null {
    try {
        const raw = new URLSearchParams(window.location.search).get('relay');
        if (!raw) return null;
        const v = raw.toLowerCase();
        if (v === 'on' || v === 'off' || v === 'auto') return v;
    } catch { /* swallow */ }
    return null;
}

function readRelayFlagFromStorage(): 'on' | 'off' | null {
    try {
        const v = window.localStorage.getItem(RELAY_STORAGE_KEY);
        if (v === 'on' || v === 'off') return v;
    } catch { /* swallow */ }
    return null;
}

function writeRelayFlagToStorage(v: 'on' | 'off' | null): boolean {
    try {
        if (v == null) window.localStorage.removeItem(RELAY_STORAGE_KEY);
        else window.localStorage.setItem(RELAY_STORAGE_KEY, v);
        return true;
    } catch {
        return false;
    }
}

// URL→storage handoff runs once per page load. If the storage write
// succeeds, the in-memory copy is cleared and storage becomes the source
// of truth (so operator DevTools flips work). If the write fails (Safari
// private mode, sandboxed iframe, enterprise policy), in-memory keeps
// the URL value and overrides whatever stale value is still readable
// from storage — that's the only path through which a fresh operator
// override can apply in a storage-restricted environment.
function processUrlOverrideOnce(): void {
    if (urlOverrideProcessed) return;
    urlOverrideProcessed = true;
    const url = readRelayFlagFromUrl();
    if (url === 'auto') {
        const ok = writeRelayFlagToStorage(null);
        // If removal failed, mark 'clear' so isForcedRelay() doesn't
        // fall back to the stale stored value. If removal succeeded,
        // storage is now empty and the default takes over.
        urlOverrideInMemory = ok ? null : 'clear';
    } else if (url === 'on' || url === 'off') {
        const ok = writeRelayFlagToStorage(url);
        urlOverrideInMemory = ok ? null : url;
    }
}

export function isForcedRelay(): boolean {
    processUrlOverrideOnce();
    // 'on'/'off' in memory: storage write failed, trust URL.
    if (urlOverrideInMemory === 'on' || urlOverrideInMemory === 'off') {
        return urlOverrideInMemory === 'on';
    }
    // 'clear' in memory: ?relay=auto requested reset but storage
    // removal failed; ignore the stale stored value and apply default.
    if (urlOverrideInMemory === 'clear') {
        return false;
    }
    const stored = readRelayFlagFromStorage();
    if (stored === 'on' || stored === 'off') return stored === 'on';
    return false;
}

/** Test-only: reset URL one-shot so the next call re-reads. */
export function __resetForcedRelayCacheForTest(): void {
    urlOverrideProcessed = false;
    urlOverrideInMemory = null;
}
